import { createContext, useState, useEffect, useRef } from 'react'
import { hasCookie, deleteCookie } from '../utils/cookie'
import { LoginPage } from '../pages/login-page'
import { LogoutPage } from '../pages/logout-page'
import { requestBaseUrl, sessionExpires } from '../utils/request'
import { useConf } from '.'
import {
  BasicButton,
  CenteredSpinner,
  InfoBox,
  SimpleModal,
  WrapperProps
} from '../components'
import { Box, Grid } from '@mui/material'

const AuthContext = createContext<{
  auth: boolean | undefined
  checkAuth: () => void
  logOut: () => void
  closeLoggedOutPage: () => void
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
}>(undefined!)

const REACT_APP_LOGOUT_URL: string | undefined =
  process.env.REACT_APP_LOGOUT_URL

export const AuthConsumer = AuthContext.Consumer

let sessionExpiredInterval: ReturnType<typeof setInterval> | null = null
const sessionExpiredWorker: Worker | undefined = window.Worker
  ? new Worker('/interval-worker.js') || undefined
  : undefined

/**
 * Provider for user info.
 * @param {React.PropsWithChildren} props
 */

const AuthInfoProvider = ({ children }: WrapperProps) => {
  const [auth, setAuth] = useState<boolean | undefined>(undefined)
  const [loggedOut, setLoggedOut] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [showSessionIsAboutToEndMessage, setShowSessionIsAboutToEndMessage] =
    useState<boolean>(false)

  const authRef = useRef(auth)
  authRef.current = auth

  const conf = useConf()

  const warnBefore = conf.session.sessionEndsWarningBeforeMilliseconds
  const pollInterval = 5000
  const logoutMarginOfCertainty = pollInterval * 3

  const checkSessionStatus = () => {
    const expires = sessionExpires?.getTime() || 0
    const now = new Date().getTime()
    const timeLeft = expires - now
    const warn = timeLeft < warnBefore

    if (authRef.current && timeLeft < logoutMarginOfCertainty) {
      console.warn('Session is ending, automatically logging out')
      logOut()
    } else if (!showSessionIsAboutToEndMessage && expires > 0 && warn) {
      setShowSessionIsAboutToEndMessage(true)
    }
  }

  useEffect(() => {
    setLoading(true)

    /* Setting auth=true by default when running Jest tests (environment has JEST_WORKER_ID)
       ...or not, if process.env.DISABLE_JEST_TEST_AUTO_AUTH is set to "yes" */
    if (
      process?.env?.JEST_WORKER_ID !== undefined &&
      process?.env?.DISABLE_JEST_TEST_AUTO_AUTH !== 'yes'
    ) {
      setAuth(true)
      setLoading(false)
    } else if (
      hasCookie('lottaAGA') ||
      (hasCookie('lotta') && process.env.NODE_ENV === 'development')
    ) {
      requestBaseUrl
        .get('/session')
        .then((p) => {
          if (p?.data && p.data === '👍') {
            setLoading(false)
            setAuth(true)
            if (sessionExpiredWorker) {
              sessionExpiredWorker.onmessage = checkSessionStatus
              sessionExpiredWorker.postMessage({
                pollInterval: pollInterval
              })
            } else {
              if (sessionExpiredInterval === null) {
                sessionExpiredInterval = setInterval(function () {
                  checkSessionStatus()
                }, pollInterval)
              }
            }
          } else {
            setAuth(false)
            setLoading(false)
          }
        })
        .catch((error) => {
          setAuth(false)
          setLoading(false)
          console.error(error)
        })
    } else {
      setAuth(false)
      setLoading(false)
      if (window.location.hash === '#loggedout') {
        window.location.hash = ''
        history.pushState(
          '',
          document.title,
          window.location.pathname + window.location.search
        )
        setLoggedOut(true)
      }
    }
    /* react-hooks/exhaustive-deps disabled, because effect is wanted to run only once at startup */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkAuth = () => {
    setLoading(true)
    requestBaseUrl
      .get('/session')
      .then((p) => {
        if (p?.data && p.data === '👍') {
          setAuth(true)
          setLoading(false)
        } else {
          setAuth(false)
          setLoading(false)
          // TODO: error or not error, that... throw new Error('No session!')
        }
      })
      .catch((error) => {
        setAuth(false)
        setLoading(false)
        console.error(error)
      })
  }

  const continueSession = () => {
    requestBaseUrl
      .get('/session')
      .then((p) => {
        if (!(p?.data && p.data === '👍')) {
          setAuth(false)
        }
      })
      .catch((error) => {
        setAuth(false)
        console.error(error)
      })
  }

  const logOut = () => {
    if (undefined !== REACT_APP_LOGOUT_URL) {
      setLoading(true)
    }

    if (sessionExpiredWorker) {
      sessionExpiredWorker.postMessage({
        clearInterval: true
      })
      sessionExpiredWorker.terminate()
    } else {
      if (sessionExpiredInterval !== null) {
        clearInterval(sessionExpiredInterval)
      }
      sessionExpiredInterval = null
    }

    requestBaseUrl
      .delete('/logout')
      .then(() => {
        deleteCookie('lottaAGA')
      })
      .catch((error) => {
        deleteCookie('lottaAGA')
        console.error(error)
      })
      .finally(() => {
        setAuth(false)
        if (undefined !== REACT_APP_LOGOUT_URL) {
          setTimeout(() => {
            window.location.href = REACT_APP_LOGOUT_URL
          }, 0)
        } else {
          setLoggedOut(true)
        }
      })
  }

  const closeLoggedOutPage = () => {
    setLoggedOut(false)
  }

  if (loading) {
    return <CenteredSpinner />
  }

  if (loggedOut) {
    return (
      <AuthContext.Provider
        value={{ auth, checkAuth, logOut, closeLoggedOutPage }}
      >
        <LogoutPage />
      </AuthContext.Provider>
    )
  } else if (!auth) {
    return <LoginPage />
  }

  const YourSessionIsAboutToEnd = () => (
    <>
      <Box mb={4} mt={2}>
        <InfoBox translationId="session.isAboutToEnd" />
      </Box>
      <Grid container direction="row" spacing={4} justifyContent="flex-end">
        <Grid item>
          <BasicButton
            onClick={() => logOut()}
            translationId="session.logOut"
          />
        </Grid>
        <Grid item>
          <BasicButton
            onClick={() => {
              setShowSessionIsAboutToEndMessage(false)
              continueSession()
            }}
            translationId="session.continue"
          />
        </Grid>
      </Grid>
    </>
  )

  return (
    <AuthContext.Provider
      value={{ auth, checkAuth, logOut, closeLoggedOutPage }}
    >
      {showSessionIsAboutToEndMessage && (
        <SimpleModal body={<YourSessionIsAboutToEnd />} isOpen={true} />
      )}
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthInfoProvider }
